<template>
  <div
    data-test="pitch-type-detail"
    class="pitch-type-detail"
    :class="{
      'single-photo': showPitchtypeDetails && pitchtype.photos.length === 1,
      'two-photos': showPitchtypeDetails && pitchtype.photos.length === 2,
      desktop: $isDesktop,
    }"
  >
    <pitch-type-photos
      v-if="showPitchtypeDetails && pitchtype.photos.length"
      :photos="pitchtype.photos"
      :use-lazy-images="useLazyImages"
      class="pitch-type-photos"
      :fullscreen-modal-hash="`pitch-${pitchtype.id}-gallery`"
    />

    <span class="pitch-info-group1">
      <super-facilities
        :super-facilities="campsiteSuperFacilities"
        :capacity-display="pitchtype.capacityDisplay"
        :is-over-capacity="isOverCapacity"
        :facilities="pitchtype.facilities"
        :restrictions="pitchtype.restrictions"
        :age-restrictions="pitchtype.ageRestrictions"
        :is-byo="pitchtype.isByo"
        :bedrooms="pitchtype.bedrooms"
        class="superfacilities"
        :pitchtype-rating="pitchtype.rating"
        :pitchtype-ratecount="pitchtype.rateCount"
      />

      <p
        v-if="pitchtype.description"
        class="pitch-intro"
        data-g-translate="pitchtype-description"
        v-html="newlinesToHtmlBR(pitchtype.description)"
      />
      <lazy-google-translate-button
        v-if="pitchtype.descriptionNeedsTranslation"
        query-selector-value="pitchtype-description"
      />

      <p v-if="pitchtype.bringBedLinenHtml" class="bed-linen">
        {{ pitchtype.bringBedLinenHtml }}
      </p>

      <unit-per-pitch
        v-if="pitchtype.isByo"
        :one-unit-per-pitch="pitchtype.oneUnitPerPitch"
        :allowed-subcategories="allowedSubcategories"
      />

      <template v-if="pitchtype.dimensions">
        <translate v-if="pitchtype.isByo" tag="h4"> Pitch size </translate>
        <translate v-else tag="h4"> Dimensions </translate>
        <pitch-type-dimensions :pitch-type="pitchtype" :with-title="false" />
      </template>

      <p v-if="pitchtype.yearOfModel">
        <translate>Year of manufacture</translate>:
        {{ pitchtype.yearOfModel }}
      </p>

      <template v-if="pitchtype.groundType">
        <translate tag="h4"> Ground type </translate>
        <p v-if="pitchtype.groundType">
          {{ pitchtype.groundType }}
        </p>
      </template>

      <bedrooms v-if="pitchtype.rooms" :rooms="pitchtype.rooms" />

      <translate
        v-if="pitchtype.hasSharedToilets"
        tag="p"
        class="unit-facilities"
      >
        No toilets in the unit: shared facilities available.
      </translate>

      <translate
        v-if="pitchtype.hasSharedShowers"
        tag="p"
        class="unit-facilities"
      >
        No showers in the unit: shared facilities available.
      </translate>

      <translate v-if="pitchtype.hasNoShowers" tag="p" class="unit-facilities">
        No showers on site.
      </translate>

      <translate v-if="pitchtype.hasNoToilets" tag="p" class="unit-facilities">
        No toilets on site.
      </translate>

      <template v-if="pitchtype.isByo && pitchtype.twinAxleAccepted != null">
        <translate v-if="pitchtype.twinAxleAccepted" tag="p">
          Twin-axle vehicles accepted
        </translate>
        <translate v-else tag="p"> Twin-axle vehicles not accepted </translate>
      </template>

      <template v-if="suitableFor.length">
        <translate tag="h4"> Suitable for </translate>
        <p>{{ suitableFor }}</p>
      </template>
    </span>

    <span class="pitch-info-group2">
      <facilities
        v-if="pitchtype.facilities"
        :extras-non-simple="pitchtype.extrasNonSimple"
        :facilities="pitchtype.facilities"
      />

      <other-restrictions
        v-if="pitchtype.restrictions"
        :restrictions="pitchtype.restrictions"
      />

      <pitch-type-offers
        v-if="pitchtype.activeOffers.length"
        :offers="pitchtype.activeOffers"
      >
        <translate tag="h4"> Current offers </translate>
      </pitch-type-offers>

      <slot v-if="$isDesktop" name="extras" />
    </span>

    <span class="pitch-info-group3">
      <slot name="availability" />

      <slot name="notify-me-above" />
      <slot name="costs-breakdown" />
      <slot name="notify-me-below" />

      <slot v-if="!$isDesktop" name="extras" />
    </span>
    <nuxt-to-meta :nuxt2meta="head" />
  </div>
</template>

<script lang="ts">
import { ComputedRef } from 'vue'
import { newlinesToHtmlBR } from '~/utility/newlinesToHtmlBR'
import {
  Component,
  Inject,
  Prop,
  Vue,
  toNative,
} from '~/utility/pu-class-decorator'
import { CalendarDay } from '../../../campsite-details/alternativeStaysFetch'
import type {
  CampsiteFacility,
  PitchType,
} from '../../../campsite-details/types'

@Component({})
class PitchTypeDetail extends Vue {
  @Prop()
  campsiteSuperFacilities: CampsiteFacility[]

  @Prop()
  pitchtype: PitchType

  @Prop()
  isOverCapacity: boolean

  @Prop({ default: false })
  showPitchtypeDetails: boolean

  @Prop({ default: true })
  useLazyImages: boolean

  @Inject({ from: 'alternativeStays', default: undefined }) alternativeStays?: {
    data: ComputedRef<CalendarDay[]>
    loading: ComputedRef<boolean>
    error: ComputedRef<Error | null>
  }

  get allowedSubcategories() {
    return this.pitchtype.allowedSubcategories
      .filter((c) => c.allowed)
      .map((c) => c.name)
  }

  get suitableFor() {
    return this.pitchtype.allowedSubcategories
      .map((category) =>
        category.allowed ? category.name : `(${category.name} not allowed)`,
      )
      .join(', ')
  }

  get head() {
    return {
      script: [
        {
          type: 'application/ld+json',
          body: true,
          json: {
            '@context': 'https://schema.org',
            '@type': this.pitchtype.isByo ? 'CampingPitch' : 'Accommodation',
            name: this.pitchtype.name,
          },
        },
      ],
    }
  }

  newlinesToHtmlBR(stringToUnify) {
    return newlinesToHtmlBR(stringToUnify)
  }
}

export default toNative(PitchTypeDetail)
</script>
<style scoped>
.pitch-type-detail :deep(table),
.pitch-type-detail :deep(p) {
  font-size: var(--body);
}

.pitch-type-detail :deep(h4) {
  color: var(--colour-dark-blue);
  font-size: 1em;
  margin: 1.3em 0 0.25em 0;
}

.pitch-type-detail :deep(.extras h4) {
  margin-top: 0.5em;
}

.pitch-info-group1 .superfacilities + :deep(h4) {
  margin-top: 0.4em;
}

.pitch-type-detail :deep(.google-translate) {
  margin-top: 0;
}

.pitch-type-photos {
  margin: 0 -1em 0;
}

.single-photo :deep(.pitch-type-photos) {
  margin: 1em 1em 0 0;
  display: inline-flex;
  float: left;
  position: relative;
}

.single-photo :deep(.photo-wrapper) {
  max-width: 33vw;
  border: none;
}

.single-photo :deep(.pitch-type-photos .photograph.icon),
.single-photo :deep(.pitch-type-photos .photo-count:not(.photo-count-fs)) {
  display: none;
}

.single-photo :deep(.photos-row) {
  width: 33vw;
  border-radius: 0.3em;
  overflow: hidden;
}

.single-photo :deep(.photo-wrapper > img) {
  width: 100%;
  max-width: none;
}

.single-photo :deep(.facilities .facility) {
  max-width: 100%;
}

p {
  font-size: var(--body);
}

.bed-linen {
  margin: 0 0 0.5em 0;
}

.pitch-intro {
  margin: 0 0 0.75em 0;
}

.pitch-type-detail :deep(.one-unit-per-pitch) {
  margin: 0 0 1em;
}

.pitch-type-detail :deep(.restriction .no-dogs-allowed),
.pitch-type-detail :deep(img.no-dogs-allowed) {
  height: 1.5em;
  width: auto;
  vertical-align: middle;
}

.unit-facilities {
  margin: 0;
}

.pitch-type-detail :deep(.pt-notify-me) {
  margin: 0 -1em;
  padding: 0.5em 1em 0;
}

/* desktop & tablet */
.pitch-type-detail.desktop {
  display: grid;
  grid-template-columns: minmax(14ch, 28%) minmax(14ch, auto) minmax(24em, 28em);
  grid-template-rows: auto 1fr;
  column-gap: 4%;
  align-items: start;
  justify-items: start;
}

.pitch-type-detail.desktop :deep(table),
.pitch-type-detail.desktop :deep(p),
.pitch-type-detail.desktop :deep(.unit-per-pitch) {
  font-size: inherit;
}

.pitch-type-detail.desktop :deep(.one-unit-per-pitch) {
  margin: 1em 0;
}

.desktop .pitch-type-photos {
  margin: 0 0 1em 0;
  grid-row: 1/2;
  grid-column: span 2;
}

.desktop .pitch-info-group1 {
  grid-row: 2/3;
  grid-column: 1/2;
  padding-bottom: 5em;
}

.desktop .pitch-info-group1 .superfacilities + :deep(h4) {
  margin-top: 1em;
}

.desktop .pitch-info-group2 {
  grid-row: 2/3;
  grid-column: 2/3;
  padding-bottom: 5em;
}

.desktop .pitch-info-group3 {
  grid-row: 1/3;
  grid-column: 3/4;
}

.desktop .pitch-info-group1 :deep(.facilities) {
  align-items: flex-start;
  column-gap: 3em;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  margin-top: 1.3em;
  row-gap: 0.75em;
  padding: 0;
}

.desktop .pitch-type-photos :deep(.photo-wrapper),
.desktop .pitch-type-photos :deep(.photos-row) {
  min-height: auto;
  border-right: none;
}

.desktop .pitch-type-photos :deep(.photos-row) {
  column-gap: 0.7em;
  background-color: white;
}

.desktop .pitch-type-photos :deep(.photo-wrapper img:not(.icon)) {
  border-radius: 0.6em;
}

.desktop .pitch-type-photos :deep(.photo-wrapper) {
  cursor: zoom-in;
}

.desktop.two-photos :deep(.photo-wrapper) {
  max-width: 50%;
}

.desktop .pitch-type-photos :deep(.photo-count-fullscreen) {
  padding: 0 0.7em 0.5em;
}

.pitch-type-detail.desktop :deep(.pt-notify-me) {
  border-radius: 0 0 0.6em 0.6em;
  margin: 0 0 -1em 0;
  padding: 0 1.4em 1.4em;
}

.desktop .single-photo :deep(.pitch-type-photos) {
  margin: 0 0 1em 0;
  float: none;
  width: 100%;
}

.desktop .single-photo :deep(.photo-wrapper) {
  max-width: calc(100% / 3);
}

.desktop .single-photo :deep(.photos-row) {
  width: 100%;
}

/* small tablet */
@media (max-width: 900px) {
  .pitch-type-detail.desktop {
    display: block;
  }

  .desktop :deep(.dated-extras.extras-wrapper) {
    margin: 1em 0 2em 0;
  }
}
</style>
